import React, { useState, useRef } from 'react';

import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import PrivacyNotice from '../components/PrivacyNotice';

import ReCAPTCHA from 'react-google-recaptcha';
const RECAPTCHA_SITE_KEY = '6LcJqessAAAAAPjYrkXPMaVPSYJUZSu20Jh-R5Oa';
const WHATSAPP_NUMBER = '447839282633';

const SERVICES = [
  'Garden maintenance','Lawn care','Hedge cutting','Landscaping & design',
  'Fencing','Planting & borders','Excavation & levelling','Clearance','Other',
];

const SERVICE_FIELDS = {
  'Lawn care': [
    { key: 'lawnAreaSqm', label: 'Approximate lawn area', type: 'number', placeholder: 'e.g. 50', unit: 'm²' },
    { key: 'lawnFrequency', label: 'How often?', type: 'select', options: ['One-off cut','Weekly','Fortnightly','Monthly','Not sure'] },
    { key: 'lawnCondition', label: 'Current condition', type: 'select', options: ['Well maintained','Needs a tidy','Overgrown','Very overgrown'] },
  ],
  'Garden maintenance': [
    { key: 'gardenAreaSqm', label: 'Approximate garden area', type: 'number', placeholder: 'e.g. 100', unit: 'm²' },
    { key: 'maintenanceFrequency', label: 'How often?', type: 'select', options: ['One-off','Weekly','Fortnightly','Monthly','Not sure'] },
  ],
  'Hedge cutting': [
    { key: 'hedgeHeightM', label: 'Hedge height', type: 'number', placeholder: 'e.g. 2.5', unit: 'm' },
    { key: 'hedgeLengthM', label: 'Hedge length', type: 'number', placeholder: 'e.g. 15', unit: 'm' },
    { key: 'hedgeSides', label: 'Sides to cut', type: 'select', options: ['One side','Both sides','Top only','All round'] },
    { key: 'hedgeAccess', label: 'Access difficulty', type: 'select', options: ['Easy — open access','Some obstacles','Restricted access','Very difficult'] },
  ],
  'Fencing': [
    { key: 'fenceLengthM', label: 'Fence length', type: 'number', placeholder: 'e.g. 20', unit: 'm' },
    { key: 'fenceType', label: 'Fence type', type: 'select', options: ['Closeboard','Panel','Post and rail','Post and wire','Not sure yet'] },
    { key: 'fenceExisting', label: 'Existing fence to remove?', type: 'select', options: ['No — new installation','Yes — remove and replace'] },
  ],
  'Excavation & levelling': [
    { key: 'excavationAreaSqm', label: 'Area to excavate', type: 'number', placeholder: 'e.g. 30', unit: 'm²' },
    { key: 'excavationDepthM', label: 'Approximate depth', type: 'number', placeholder: 'e.g. 0.3', unit: 'm' },
    { key: 'spoilRemoval', label: 'Spoil removal needed?', type: 'select', options: ['Yes — remove from site','No — spread on site','Not sure'] },
  ],
  'Landscaping & design': [
    { key: 'landscapeAreaSqm', label: 'Approximate area', type: 'number', placeholder: 'e.g. 200', unit: 'm²' },
    { key: 'landscapeScope', label: 'Scope of work', type: 'select', options: ['Design only','Design and planting','Full landscaping','Not sure yet'] },
    { key: 'landscapeBudget', label: 'Approximate budget', type: 'select', options: ['Under £500','£500–£1,000','£1,000–£5,000','£5,000+','Flexible'] },
  ],
  'Clearance': [
    { key: 'clearanceType', label: 'What needs clearing?', type: 'select', options: ['Garden waste','Overgrown vegetation','Rubble and hardcore','Mixed waste','Other'] },
    { key: 'clearanceAreaSqm', label: 'Approximate area', type: 'number', placeholder: 'e.g. 50', unit: 'm²' },
    { key: 'clearanceRemoval', label: 'Waste removal needed?', type: 'select', options: ['Yes — remove from site','No — leave on site','Not sure'] },
  ],
};

const HOURLY_SERVICES = ['Lawn care','Garden maintenance','Clearance','Planting & borders'];

export default function QuoteRequest() {
  const [form, setForm] = useState({ name:'',email:'',phone:'',address:'',services:[],description:'',preferredDate:'',pricingPreference:'fixed',dynamicFields:{},jobType:'one-off' });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const recaptchaRef = useRef(null);

  const showHourlyOption = form.services.some(s => HOURLY_SERVICES.includes(s));
  const dynamicFieldsToShow = form.services.filter(s => SERVICE_FIELDS[s]).flatMap(s => SERVICE_FIELDS[s].map(f => ({ ...f, service: s })));

  function toggle(service) {
    setForm(f => ({ ...f, services: f.services.includes(service) ? f.services.filter(s => s !== service) : [...f.services, service] }));
  }
  function setDynamic(key, value) { setForm(f => ({ ...f, dynamicFields: { ...f.dynamicFields, [key]: value } })); }

  async function handleSubmit(e) {
    e.preventDefault(); setError('');
    if (!form.name || !form.email || !form.description) { setError('Please fill in your name, email and job description.'); return; }
    if (!privacyAccepted) { setError('Please read and accept the Privacy Notice to continue.'); return; }
    if (!captchaToken) { setError('Please complete the security check.'); return; }
    setStatus('sending');
    try {
      await addDoc(collection(db, 'quotes'), { ...form, email: form.email.trim().toLowerCase(), status: 'pending', privacyAccepted: true, privacyAcceptedAt: serverTimestamp(), createdAt: serverTimestamp() });
      setStatus('sent');
    } catch (err) {
      setError('Something went wrong. Please try again.'); setStatus('idle');
      recaptchaRef.current?.reset(); setCaptchaToken(null);
    }
  }

  const whatsappMsg = encodeURIComponent(`Hi Dean, I've just submitted a quote request on the Garden & Groundworks website. My name is ${form.name || '[your name]'}. I'm sending some photos of the site to help with the quote.`);
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`;

  if (status === 'sent') return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:16, background:'var(--bg)' }}>
      <div style={{ width:'100%', maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:32, marginBottom:8 }}>🌿</div>
          <h1 style={{ fontSize:'1.3rem', fontWeight:700, color:'var(--green-dark)' }}>Garden & Groundworks</h1>
        </div>
        <div className="card">
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <div style={{ fontSize:48, marginBottom:12 }}>🌱</div>
            <h2 style={{ marginBottom:8, color:'var(--green-dark)' }}>Quote request sent!</h2>
            <p style={{ color:'var(--text-secondary)', lineHeight:1.6, fontSize:'0.95rem' }}>
              Thank you, <strong>{form.name}</strong>. Dean will review your request and be in touch within 24 hours.
            </p>
          </div>
          <hr className="divider" />
          <div style={{ background:'#E7F8EF', borderRadius:12, padding:16, border:'1px solid #A8D5B5', marginBottom:16 }}>
            <div style={{ fontWeight:700, color:'#1B4F2A', marginBottom:6, fontSize:'0.95rem' }}>📸 Got photos of the site?</div>
            <p style={{ color:'#2E5C3E', fontSize:'1rem', lineHeight:1.6, marginBottom:12 }}>
              Send them to Dean on WhatsApp — good photos help him assess the job and may avoid the need for a site visit before quoting.
            </p>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
              style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, background:'#25D366', color:'white', borderRadius:10, padding:'12px 20px', fontWeight:700, fontSize:'0.95rem', textDecoration:'none' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Send photos on WhatsApp
            </a>
          </div>
          <button className="btn-secondary" onClick={() => { setStatus('idle'); setForm({ name:'',email:'',phone:'',address:'',services:[],description:'',preferredDate:'',pricingPreference:'fixed',dynamicFields:{},jobType:'one-off' }); setPrivacyAccepted(false); setCaptchaToken(null); }}>
            Submit another request
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ background:'var(--bg)', minHeight:'100vh' }}>
      <div style={{ background:'var(--green-dark)', padding:'24px 20px 20px', textAlign:'center' }}>
        <div style={{ fontSize:32, marginBottom:8 }}>🌿</div>
        <h1 style={{ color:'white', fontSize:'1.3rem', fontWeight:700, marginBottom:4 }}>Garden & Groundworks</h1>
        <p style={{ color:'rgba(255,255,255,0.7)', fontSize:'0.85rem' }}>Guernsey gardening & landscaping</p>
      </div>

      <div className="page-wrap" style={{ paddingTop:24, maxWidth:480 }}>
        <div style={{ marginBottom:24 }}>
          <h2 className="section-title">Request a quote</h2>
          <p style={{ color:'var(--text-secondary)', fontSize:'0.95rem' }}>No obligation. Dean will review your request and get back to you within 24 hours.</p>
        </div>

        {error && <div className="error-msg">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="card" style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:'1rem', marginBottom:16, color:'var(--text-secondary)', fontWeight:600 }}>Your details</h3>
            <div className="form-group"><label>Full name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name:e.target.value }))} placeholder="Your name" /></div>
            <div className="form-group"><label>Email address *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email:e.target.value }))} placeholder="your@email.com" /></div>
            <div className="form-group"><label>Phone number</label><input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone:e.target.value }))} placeholder="01481 000000" /></div>
            <div className="form-group" style={{ marginBottom:0 }}><label>Site address</label><input value={form.address} onChange={e => setForm(f => ({ ...f, address:e.target.value }))} placeholder="Where is the work?" /></div>
          </div>

          <div className="card" style={{ marginBottom:16 }}>
            <h3 style={{ fontSize:'1rem', marginBottom:16, color:'var(--text-secondary)', fontWeight:600 }}>What do you need?</h3>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:16 }}>
              {SERVICES.map(s => (
                <button key={s} type="button" onClick={() => toggle(s)} style={{ padding:'8px 14px', borderRadius:20, fontSize:'0.85rem', fontWeight:500, background:form.services.includes(s) ? 'var(--green-mid)' : 'white', color:form.services.includes(s) ? 'white' : 'var(--text-secondary)', border:`1.5px solid ${form.services.includes(s) ? 'var(--green-mid)' : 'var(--border)'}`, width:'auto', transition:'all 0.15s' }}>{s}</button>
              ))}
            </div>

            {dynamicFieldsToShow.length > 0 && (
              <div style={{ background:'var(--green-pale)', borderRadius:10, padding:16, marginBottom:16, border:'1px solid var(--green-light)' }}>
                <div style={{ fontSize:'0.82rem', color:'var(--green-dark)', fontWeight:600, marginBottom:12 }}>📐 Job details — helps Dean quote accurately</div>
                {dynamicFieldsToShow.map(field => (
                  <div key={field.key} className="form-group" style={{ marginBottom:12 }}>
                    <label style={{ color:'var(--green-dark)' }}>{field.label}</label>
                    {field.type === 'select' ? (
                      <select value={form.dynamicFields[field.key] || ''} onChange={e => setDynamic(field.key, e.target.value)}>
                        <option value="">Select...</option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <div style={{ position:'relative' }}>
                        <input type={field.type} value={form.dynamicFields[field.key] || ''} onChange={e => setDynamic(field.key, e.target.value)} placeholder={field.placeholder} style={{ paddingRight:field.unit ? 48 : 16 }} />
                        {field.unit && <span style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)', fontSize:'0.85rem', pointerEvents:'none' }}>{field.unit}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {showHourlyOption && (
              <div style={{ background:'var(--gold-pale)', borderRadius:10, padding:'14px 16px', marginBottom:16, border:'1px solid var(--gold)' }}>
                <div style={{ fontSize:'0.82rem', color:'#7D4A00', fontWeight:600, marginBottom:10 }}>💷 Pricing preference</div>
                <div style={{ display:'flex', gap:8 }}>
                  {[{ value:'fixed', label:'Fixed price' },{ value:'hourly', label:'Hourly rate' },{ value:'either', label:'Advise me' }].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setForm(f => ({ ...f, pricingPreference:opt.value }))}
                      style={{ flex:1, padding:'9px 4px', borderRadius:8, fontSize:'0.82rem', fontWeight:500, width:'auto', background:form.pricingPreference === opt.value ? 'var(--gold)' : 'white', color:form.pricingPreference === opt.value ? 'white' : 'var(--text-secondary)', border:`1.5px solid ${form.pricingPreference === opt.value ? 'var(--gold)' : 'var(--border)'}` }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:'0.82rem', fontWeight:600, color:'var(--text-secondary)', marginBottom:10 }}>🔄 How often is this needed?</div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                {[
                  { value:'one-off', label:'One-off' },
                  { value:'weekly', label:'Weekly' },
                  { value:'fortnightly', label:'Fortnightly' },
                  { value:'monthly', label:'Monthly' },
                ].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm(f => ({ ...f, jobType:opt.value }))}
                    style={{ padding:'8px 14px', borderRadius:20, fontSize:'0.85rem', fontWeight:500, width:'auto',
                      background:form.jobType === opt.value ? 'var(--green-mid)' : 'white',
                      color:form.jobType === opt.value ? 'white' : 'var(--text-secondary)',
                      border:`1.5px solid ${form.jobType === opt.value ? 'var(--green-mid)' : 'var(--border)'}`,
                      transition:'all 0.15s' }}>
                    {opt.value !== 'one-off' ? '🔄 ' : ''}{opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group"><label>Describe the job *</label><textarea rows={4} value={form.description} onChange={e => setForm(f => ({ ...f, description:e.target.value }))} placeholder="Tell us what you need done, any access notes, approximate size of area..." style={{ resize:'vertical' }} /></div>
            <div className="form-group" style={{ marginBottom:0 }}><label>Preferred start date (optional)</label><input type="date" value={form.preferredDate} onChange={e => setForm(f => ({ ...f, preferredDate:e.target.value }))} /></div>
          </div>

          <div style={{ background:'#E7F8EF', borderRadius:10, padding:'12px 16px', border:'1px solid #A8D5B5', marginBottom:16, display:'flex', alignItems:'flex-start', gap:10 }}>
            <span style={{ fontSize:20, flexShrink:0 }}>📸</span>
            <p style={{ fontSize:'0.95rem', color:'#2E5C3E', lineHeight:1.5, margin:0 }}>
              After submitting you'll get a link to send site photos to Dean on WhatsApp — good photos help him quote accurately and may avoid a site visit.
            </p>
          </div>

          <PrivacyNotice onAccept={() => setPrivacyAccepted(true)} onDecline={() => setPrivacyAccepted(false)} />

          <div style={{ marginBottom:16, display:'flex', justifyContent:'center' }}>
            <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} onChange={token => setCaptchaToken(token)} onExpired={() => setCaptchaToken(null)} theme="light" />
          </div>

          <button type="submit" className="btn-primary" disabled={status === 'sending' || !privacyAccepted || !captchaToken}>
            {status === 'sending' ? 'Sending...' : 'Send quote request'}
          </button>

          <p style={{ textAlign:'center', marginTop:14, fontSize:'0.95rem', color:'var(--text-muted)' }}>
            Already have an account?{' '}<a href="/login" style={{ fontWeight:600 }}>Sign in</a>{' '}to track your jobs.
          </p>
        </form>
      </div>
    </div>
  );
}
